import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/AuthContext";
import { useI18n } from "@/i18n";
import { apiError } from "@/api";
import { UserRound, Hammer, MailCheck, CheckCircle2 } from "lucide-react";

const LANG_NAMES = { de: "Deutsch", en: "English", fr: "Français", ar: "العربية" };

export default function Register() {
  const { register } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "craftsman" ? "craftsman" : "customer");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailLang, setEmailLang] = useState(lang || "fr");
  const [loading, setLoading] = useState(false);
  const [ack, setAck] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await register({ name, email, password, role, lang: emailLang, accept_terms: true });
      if (data && data.email_verification_required) {
        setSent(true);
      } else {
        toast.success(t("auth.registerBtn"));
        nav("/dashboard");
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const roleBtn = (value, Icon, label) => (
    <button type="button" data-testid={`role-${value}`} onClick={() => setRole(value)}
      className={`flex-1 rounded-xl border-2 p-4 text-left transition-colors ${role === value ? "border-[#C65338] bg-[#C65338]/5" : "border-[#EBDDD3] hover:border-[#DCC2B3]"}`}>
      <Icon className={`w-6 h-6 mb-2 ${role === value ? "text-[#C65338]" : "text-[#A89A90]"}`} />
      <div className="text-sm font-semibold text-[#3A2E28]">{label}</div>
    </button>
  );

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-16">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <span className="inline-flex w-12 h-12 rounded-2xl bg-[#C65338] items-center justify-center mb-4">
            <Hammer className="w-6 h-6 text-white" />
          </span>
          <h1 className="font-display text-3xl font-black tracking-tight text-[#2D231F]">{t("auth.registerTitle")}</h1>
        </div>
        {sent ? (
          <div className="bg-white rounded-2xl border border-[#EBDDD3] p-8 text-center" data-testid="verify-sent">
            <MailCheck className="w-12 h-12 text-[#5C6646] mx-auto mb-4" />
            <h2 className="font-display text-xl font-bold text-[#2D231F] mb-2">{t("verify.sentTitle")}</h2>
            <p className="text-[#8C7A70] text-sm">{t("verify.sentMsg")} <strong className="text-[#5A4B45]">{email}</strong></p>
          </div>
        ) : (
        <>
        <div data-testid={role === "customer" ? "reg-free-customer" : "reg-info-craftsman"} className={`rounded-2xl border p-5 mb-4 flex gap-3 ${role === "customer" ? "bg-[#EEF3E6] border-[#5C6646]" : "bg-[#FFF4E5] border-[#E69625]"}`}>
          <CheckCircle2 className={`w-6 h-6 shrink-0 ${role === "customer" ? "text-[#5C6646]" : "text-[#E69625]"}`} />
          <div><p className="font-bold text-[#2D231F]">{t(role === "customer" ? "reg.custTitle" : "reg.craftTitle")}</p><p className="text-sm text-[#5A4B45] mt-1">{t(role === "customer" ? "reg.custBody" : "reg.craftBody")}</p></div>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#EBDDD3] p-8 space-y-5">
          <div className="flex gap-3">
            {roleBtn("customer", UserRound, t("auth.role.customer"))}
            {roleBtn("craftsman", Hammer, t("auth.role.craftsman"))}
          </div>
          <div className="space-y-2">
            <Label>{t("auth.name")}</Label>
            <Input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} className="rounded-lg" />
          </div>
          <div className="space-y-2">
            <Label>{t("auth.email")}</Label>
            <Input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-lg" />
          </div>
          <div className="space-y-2">
            <Label>{t("auth.password")}</Label>
            <Input data-testid="register-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-lg" />
          </div>
          <div className="space-y-2">
            <Label>{t("auth.emailLang")}</Label>
            <Select value={emailLang} onValueChange={setEmailLang}>
              <SelectTrigger data-testid="register-lang" className="rounded-lg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(LANG_NAMES).map((l) => (
                  <SelectItem key={l} value={l} data-testid={`register-lang-${l}`}>{LANG_NAMES[l]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#A89A90]">{t("auth.emailLangHint")}</p>
          </div>
          <label className="flex items-start gap-2 text-xs text-[#5A4B45] cursor-pointer">
            <input type="checkbox" data-testid="register-accept-terms" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#C65338] shrink-0" />
            <span>
              {t("auth.acceptTerms")}{" "}
              <Link to="/legal" target="_blank" className="text-[#C65338] font-semibold underline">{t("footer.legal")}</Link>
              {" · "}
              <Link to="/privacy" target="_blank" className="text-[#C65338] font-semibold underline">{t("footer.privacy")}</Link>
            </span>
          </label>
          <Button data-testid="register-submit" disabled={loading || !ack} type="submit" className="w-full rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white h-11 font-semibold disabled:opacity-50">
            {t("auth.registerBtn")}
          </Button>
          <p className="text-sm text-center text-[#8C7A70]">
            {t("auth.haveAccount")} <Link to="/login" className="text-[#C65338] font-semibold">{t("nav.login")}</Link>
          </p>
        </form>
        </>
        )}
      </motion.div>
    </div>
  );
}